import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AcedSelect, {
	SelectOption,
} from "../../components/essentials/input/select/AcedSelect";
import AcedTextArea from "../../components/essentials/input/text-area/AcedTextArea";
import AcedInput from "../../components/essentials/input/text/AcedInput";
import StepNavigation from "../../components/essentials/step-navigation/StepNavigation";
import Switch from "../../components/essentials/switch/Switch";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import { TeamsApiRoutes } from "../../shared/api-routes/member/teams.api-routes";
import http from "../../shared/utils/http";
import AcedImageInput from "./AcedImageInput";
import "./CreateTeam.scss";

interface FormValues {
	name: string;
	sport: string;
	category: string;
	website: string;
	country: string;
	city: string;
	league: string;
	juniorLeague: boolean;
	slogan: string;
	image: any;
}

const CreateTeam: React.FC = () => {
	const {
		register,
		control,
		handleSubmit,
		watch,
		trigger,
		formState: { isValid },
	} = useForm<FormValues>({ mode: "onChange" });
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [currentStep, setCurrentStep] = useState(1);
	const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([1]));
	const [sports, setSports] = useState<SelectOption[]>([]);
	const [categories, setCategories] = useState<SelectOption[]>([]);
	const [leagues, setLeagues] = useState<SelectOption[]>([]);
	const [compressedImage, setCompressedImage] = useState<File | null>(null);
	const watchSport = watch("sport");
	const watchCategory = watch("category");

	const stepTitles = [t("createTeam.step1.title"), t("createTeam.step2.title")];

	useEffect(() => {
		http.get("/utils/sports").then((response) => {
			const filteredSports = response.filter(
				(sport: { name: string; value: string }) =>
					sport.value === "volleyball",
			);
			setSports(
				filteredSports.map(
					(sport: { name: string; value: string }) =>
						({
							value: sport.value,
							label: t("sportData.sportsLabels." + sport.value, sport.name),
						}) as SelectOption,
				),
			);
		});
	}, []);

	useEffect(() => {
		if (!watchSport) {
			setCategories([]);
			return;
		}

		const sportLabel = t("sportData.sportsLabels." + watchSport);
		setCategories([
			{ value: "M", label: `Male ${sportLabel}` },
			{ value: "F", label: `Female ${sportLabel}` },
		]);
	}, [watchSport, t]);

	useEffect(() => {
		if (!watchSport || !watchCategory) {
			setLeagues([]);
			return;
		}

		http.get(`/utils/leagues?category=${watchCategory}`).then((response) => {
			const leagueOptions: SelectOption[] = response.map(
				(value: { name: string; category: string }) =>
					({
						value: value.name,
						label: value.name,
					}) as SelectOption,
			);

			setLeagues(leagueOptions);
		});
	}, [watchSport, watchCategory]);

	const onSubmit = (data: FormValues) => {
		// Find the selected league to get its name
		const selectedLeague = leagues.find(
			(league) => league.value === data.league,
		);

		const payload: any = {
			name: data.name,
			sport: data.sport,
			country: data.country,
			city: data.city,
			league: {
				name: selectedLeague?.label || data.league,
				category: data.category,
			},
			juniorLeague: data.juniorLeague,
		};

		if (data.website) payload.website = data.website;
		if (data.slogan) payload.slogan = data.slogan;

		http.post(TeamsApiRoutes.CREATE_TEAM, payload).then((res) => {
			// If there's an image, upload it separately
			if (compressedImage) {
				const formData = new FormData();
				formData.append("profilePicture", compressedImage, "profile.png");
				http
					.post(`/members/${res.id}/profile-picture`, formData, {
						headers: {
							"Content-Type": "multipart/form-data",
						},
					})
					.then(() => {
						navigate("/t/" + res.id);
					});
			} else {
				navigate("/t/" + res.id);
			}
		});
	};

	const nextStep = async () => {
		const step1Fields: (keyof FormValues)[] = [
			"name",
			"sport",
			"category",
			"country",
			"city",
			"league",
		];
		const isStep1Valid = await trigger(step1Fields);
		if (isStep1Valid) {
			setCurrentStep(2);
			setVisitedSteps((prev) => new Set([...Array.from(prev), 2]));
		}
	};

	const prevStep = () => {
		setCurrentStep(1);
	};

	const goToStep = (step: number) => {
		setCurrentStep(step);
	};

	const getStepCompletionStatus = (
		step: number,
	): "complete" | "partial" | "incomplete" => {
		if (step === 1) {
			const step1Fields: (keyof FormValues)[] = [
				"name",
				"sport",
				"category",
				"league",
			];
			const step1Values = step1Fields.map((field) => watch(field));
			const hasAllStep1Values = step1Values.every(
				(value) => value && value !== "",
			);
			return hasAllStep1Values ? "complete" : "incomplete";
		}
		return "incomplete";
	};

	return (
		<>
			<Header />
			<div className="create-team">
				<h1 className="create-team-title">{t("createTeam.title")}</h1>

				<StepNavigation
					currentStep={currentStep}
					totalSteps={2}
					onStepClick={goToStep}
					stepTitles={stepTitles}
					visitedSteps={visitedSteps}
					getStepCompletionStatus={getStepCompletionStatus}
					nextLabel={t("common.next", "Next")}
					prevLabel={t("common.previous", "Previous")}
					submitLabel={t("createTeam.buttons.create", "Create Team")}
					showNavigationButtons={true}
					onNext={nextStep}
					onPrev={prevStep}
					onSubmit={handleSubmit(onSubmit)}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="step-content-team">
							<div className="row-group">
								{currentStep === 1 && (
									<>
										<AcedInput
											label={t("createTeam.form.name.label")}
											placeholder={t("createTeam.form.name.placeholder")}
											name="name"
											control={control}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										/>
										<AcedSelect
											label={t("createTeam.form.sport.label")}
											control={control}
											name="sport"
											options={sports}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										></AcedSelect>
										<AcedSelect
											label={t("createJob.categoryLabel")}
											control={control}
											name="category"
											options={categories}
											isDisabled={!watchSport}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										></AcedSelect>
										<AcedSelect
											label={t("common.words.league")}
											control={control}
											name="league"
											options={leagues}
											isDisabled={!watchSport || !watchCategory}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										></AcedSelect>
										<div className="junior-league-switch">
											<label className="select-label">
												{t("createTeam.form.juniorLeague.label")}
											</label>
											<Switch
												control={control}
												name="juniorLeague"
												colorOne="#ffc629"
												colorTwo="#5f6d6d"
											/>
										</div>
									</>
								)}

								{currentStep === 2 && (
									<>
										<AcedInput
											label={t("createTeam.form.country.label")}
											placeholder={t("createTeam.form.country.placeholder")}
											name="country"
											control={control}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										/>
										<AcedInput
											label={t("createTeam.form.city.label")}
											placeholder={t("createTeam.form.city.placeholder")}
											name="city"
											control={control}
											rules={{
												required: `${t("login.errors.required")}`,
											}}
										/>
										<AcedInput
											label={t("createTeam.form.website.label")}
											placeholder={t("createTeam.form.website.placeholder")}
											name="website"
											control={control}
										/>
										<AcedImageInput
											label={t("createTeam.fields.chooseLogo")}
											register={register("image")}
											onImageChange={setCompressedImage}
										/>
										<div className="area-container">
											<AcedTextArea
												label={t("createTeam.form.slogan.label")}
												placeholder={t("createTeam.form.slogan.placeholder")}
												name="slogan"
												control={control}
											></AcedTextArea>
										</div>
									</>
								)}
							</div>
						</div>
					</form>
				</StepNavigation>
			</div>
			<Footer />
		</>
	);
};

export default CreateTeam;
